import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import { API } from '../../App';

const STATUS_META = {
  connected:           { label: 'Answered',   cls: 'bg-emerald-500/20 text-emerald-400' },
  no_answer:           { label: 'No Answer',  cls: 'bg-amber-500/20  text-amber-400'   },
  busy:                { label: 'Busy',        cls: 'bg-orange-500/20 text-orange-400'  },
  failed:              { label: 'Failed',      cls: 'bg-red-500/20    text-red-400'     },
  canceled:            { label: 'Canceled',    cls: 'bg-slate-500/20  text-slate-400'   },
  initiated:           { label: 'Initiated',   cls: 'bg-blue-500/20   text-blue-400'    },
  expired_by_new_scan: { label: 'Superseded',  cls: 'bg-slate-500/20  text-slate-400'   },
};

function statusMeta(status) {
  return STATUS_META[status] || { label: status || 'Unknown', cls: 'bg-slate-500/20 text-slate-400' };
}

class AdminCalls extends React.Component {
  state = {
    users: [], loading: true, page: 1, pages: 1, total: 0,
    search: '', searchInput: '',
    expandedUser: null, expandedQR: null,
    calls: [], qrDetails: null, callsLoading: false,
    callPage: 1, callPages: 1, startDate: '', endDate: ''
  };

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    const qrParam = params.get('qr');
    if (qrParam) {
      this.setState({ expandedQR: qrParam, loading: false }, () => {
        this.loadCalls();
      });
    } else {
      this.load();
    }
  }

  componentDidUpdate(_, ps) {
    if (ps.page !== this.state.page || ps.search !== this.state.search) this.load();
    if (
      this.state.expandedQR &&
      (ps.expandedQR !== this.state.expandedQR ||
       ps.callPage !== this.state.callPage ||
       ps.startDate !== this.state.startDate ||
       ps.endDate !== this.state.endDate)
    ) this.loadCalls();
  }

  load = async () => {
    this.setState({ loading: true });
    try {
      const t = localStorage.getItem('adminToken');
      const p = new URLSearchParams({ page: this.state.page, limit: 20 });
      if (this.state.search) p.append('search', this.state.search);
      const r = await axios.get(`${API}/admin/calls/users?${p}`, { headers: { Authorization: `Bearer ${t}` } });
      this.setState({ users: r.data.users, pages: r.data.pages, total: r.data.total, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
      this.setState({ loading: false });
    }
  };

  loadCalls = async () => {
    this.setState({ callsLoading: true });
    try {
      const t = localStorage.getItem('adminToken');
      const p = new URLSearchParams({ page: this.state.callPage, limit: 50 });
      if (this.state.startDate) p.append('start_date', this.state.startDate);
      if (this.state.endDate) p.append('end_date', this.state.endDate);
      const r = await axios.get(`${API}/admin/calls/qr/${this.state.expandedQR}?${p}`, { headers: { Authorization: `Bearer ${t}` } });
      this.setState({ calls: r.data.calls, qrDetails: r.data.qr_details, callPages: r.data.pages, callsLoading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load calls');
      this.setState({ callsLoading: false });
    }
  };

  render() {
    const { users, loading, page, pages, total, searchInput, expandedUser, expandedQR } = this.state;

    if (expandedQR && !expandedUser && users.length === 0) {
      return (
        <AdminLayout>
          <div className="space-y-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-white">Call Monitoring</h1>
                <p className="text-slate-400">Viewing calls for QR code</p>
              </div>
              <button onClick={() => window.location.href = '/admin/calls'} className="px-4 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-600">← Back to All Users</button>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              {this.renderCalls()}
            </div>
          </div>
        </AdminLayout>
      );
    }

    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-white">Call Monitoring</h1>
              <p className="text-slate-400">View call attempts by user &amp; QR code (read-only)</p>
            </div>
            <div className="px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
              🔒 Read-only • All views logged
            </div>
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); this.setState({ search: searchInput, page: 1 }); }}
            className="flex gap-4"
          >
            <input
              value={searchInput}
              onChange={(e) => this.setState({ searchInput: e.target.value })}
              placeholder="Search by full or partial phone..."
              className="flex-1 max-w-md bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white"
            />
            <button type="submit" className="px-6 py-3 bg-amber-500 text-slate-900 font-semibold rounded-xl">
              Search
            </button>
          </form>

          <div className="text-slate-400 text-sm">{total} registered users</div>

          <div className="space-y-3">
            {loading
              ? <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center text-slate-400">Loading...</div>
              : users.length === 0
                ? <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center text-slate-400">No users found</div>
                : users.map((u) => this.renderUser(u))}
          </div>

          {pages > 1 && (
            <div className="flex justify-between px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl">
              <button onClick={() => this.setState({ page: Math.max(1, page - 1) })} disabled={page === 1} className="text-slate-400 disabled:opacity-50">← Prev</button>
              <span className="text-slate-400">{page}/{pages}</span>
              <button onClick={() => this.setState({ page: Math.min(pages, page + 1) })} disabled={page === pages} className="text-slate-400 disabled:opacity-50">Next →</button>
            </div>
          )}
        </div>
      </AdminLayout>
    );
  }

  renderUser(u) {
    const { expandedUser } = this.state;
    return (
      <div key={u.id} className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <button
          onClick={() => this.setState({ expandedUser: expandedUser === u.id ? null : u.id, expandedQR: null })}
          className="w-full flex justify-between items-center p-4 hover:bg-slate-800/80"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center text-slate-400">👤</div>
            <div className="text-left">
              <p className="text-white font-mono">{u.phone}</p>
              <p className="text-slate-500 text-sm">
                Joined {new Date(u.created_at).toLocaleDateString('en-IN')} • {u.qr_codes.length} QR(s)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={u.is_enabled ? 'text-emerald-400 text-xs' : 'text-red-400 text-xs'}>
              {u.is_enabled ? 'Active' : 'Disabled'}
            </span>
            <span className="text-slate-400">{expandedUser === u.id ? '▼' : '▶'}</span>
          </div>
        </button>
        {expandedUser === u.id && this.renderQRList(u.qr_codes)}
      </div>
    );
  }

  renderQRList(qrCodes) {
    if (qrCodes.length === 0)
      return <div className="border-t border-slate-700 bg-slate-900/50 p-4 text-slate-500 text-center">No QR codes</div>;
    return (
      <div className="border-t border-slate-700 bg-slate-900/50">
        {qrCodes.map((qr) => this.renderQR(qr))}
      </div>
    );
  }

  renderQR(qr) {
    const { expandedQR } = this.state;
    const statusClass = qr.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400';
    return (
      <div key={qr.qr_id} className="border-b border-slate-700/50 last:border-0">
        <button
          onClick={() => this.setState({ expandedQR: expandedQR === qr.qr_id ? null : qr.qr_id, callPage: 1, calls: [], qrDetails: null })}
          className="w-full flex justify-between items-center p-4 pl-8 hover:bg-slate-800/50"
        >
          <div className="flex items-center gap-3">
            <span className="text-slate-500">📱</span>
            <div className="text-left">
              <p className="text-white">
                {qr.vehicle_number}
                {qr.car_nickname && <span className="text-slate-500"> ({qr.car_nickname})</span>}
              </p>
              <p className="text-slate-500 text-sm font-mono">{qr.qr_id.substring(0, 12)}...</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-2 py-1 rounded text-xs ${statusClass}`}>{qr.status}</span>
            <span className="text-slate-400 text-sm">📞 {qr.call_count}</span>
            <span className="text-slate-400">{expandedQR === qr.qr_id ? '▼' : '▶'}</span>
          </div>
        </button>
        {expandedQR === qr.qr_id && this.renderCalls()}
      </div>
    );
  }

  renderCalls() {
    const { qrDetails, calls, callsLoading, callPage, callPages, startDate, endDate } = this.state;
    return (
      <div className="bg-slate-900/80 border-t border-slate-700/50 p-4 pl-12">
        {qrDetails && (
          <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-slate-800/50 rounded-lg text-sm">
            <div><p className="text-slate-500">Owner</p><p className="text-white font-mono">{qrDetails.owner_phone}</p></div>
            <div><p className="text-slate-500">Vehicle</p><p className="text-white">{qrDetails.vehicle_number}</p></div>
            <div><p className="text-slate-500">QR Status</p><p className="text-white capitalize">{qrDetails.status}</p></div>
          </div>
        )}

        <div className="flex gap-4 mb-4">
          <input type="date" value={startDate} onChange={(e) => this.setState({ startDate: e.target.value, callPage: 1 })} className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" />
          <input type="date" value={endDate} onChange={(e) => this.setState({ endDate: e.target.value, callPage: 1 })} className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" />
          {(startDate || endDate) && (
            <button onClick={() => this.setState({ startDate: '', endDate: '', callPage: 1 })} className="text-red-400 text-sm">✕ Clear</button>
          )}
        </div>

        {callsLoading
          ? <div className="text-center py-4 text-slate-400">Loading...</div>
          : calls.length === 0
            ? <div className="text-center py-4 text-slate-500">No calls found</div>
            : <div className="space-y-2">{calls.map((c, i) => this.renderCall(c, i))}</div>}

        {callPages > 1 && (
          <div className="flex justify-between mt-4 pt-4 border-t border-slate-700/50">
            <button onClick={() => this.setState({ callPage: Math.max(1, callPage - 1) })} disabled={callPage === 1} className="text-slate-400 text-sm disabled:opacity-50">← Prev</button>
            <span className="text-slate-500 text-sm">{callPage}/{callPages}</span>
            <button onClick={() => this.setState({ callPage: Math.min(callPages, callPage + 1) })} disabled={callPage === callPages} className="text-slate-400 text-sm disabled:opacity-50">Next →</button>
          </div>
        )}
      </div>
    );
  }

  renderCall(c, i) {
    const { label, cls } = statusMeta(c.status);
    const duration = c.call_duration != null ? `${c.call_duration}s` : null;
    return (
      <div key={i} className="flex gap-3 p-3 bg-slate-800/50 rounded-lg items-start">
        <span className="text-blue-400 mt-0.5">📞</span>
        <div className="flex-1">
          <div className="flex flex-wrap gap-2 mb-1 items-center">
            <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{label}</span>
            <span className="text-slate-500 text-xs">from {c.scanner_phone || 'Unknown'}</span>
            {duration && <span className="text-slate-500 text-xs">· {duration}</span>}
            <span className="text-slate-500 text-xs capitalize">· {c.provider || 'unknown'}</span>
          </div>
          <p className="text-slate-600 text-xs">{new Date(c.created_at).toLocaleString('en-IN')}</p>
          {c.ended_at && (
            <p className="text-slate-700 text-xs">Ended {new Date(c.ended_at).toLocaleString('en-IN')}</p>
          )}
        </div>
      </div>
    );
  }
}

export default AdminCalls;
